import viewBackImg from 'assets/images/aistudio/views/view_back.png';
import viewBottomImg from 'assets/images/aistudio/views/view_bottom.png';
import viewFrontImg from 'assets/images/aistudio/views/view_front.png';
import viewLeftImg from 'assets/images/aistudio/views/view_left.png';
import viewLeft45Img from 'assets/images/aistudio/views/view_left_45.png';
import viewRightImg from 'assets/images/aistudio/views/view_right.png';
import viewRight45Img from 'assets/images/aistudio/views/view_right_45.png';
import viewTopImg from 'assets/images/aistudio/views/view_top.png';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { Icon } from '../../../../widgets/icon';
import { showErrorNotification } from '../../../../widgets/notificationToaster';
import { MAX_3D_IMAGE_DIMENSION, MAX_3D_UPLOAD_SIZE_BYTES, MAX_3D_UPLOAD_SIZE_MB } from '../../constants';
import { ModelGeneration3DModel } from '../../types';
import { getImageDimensions } from '../../utils/imageHelpers';

export type ViewSlotId = 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | 'left_front' | 'right_front';

interface ViewSlotDefinition {
    id: ViewSlotId;
    label: string;
    required?: boolean;
    thumbnail: string;
}

export interface MultiViewInputData {
    file: File;
    viewType: ViewSlotId;
}

export const VIEW_SLOTS: ViewSlotDefinition[] = [
    { id: 'front', label: 'Front', required: true, thumbnail: viewFrontImg },
    { id: 'back', label: 'Back', thumbnail: viewBackImg },
    { id: 'left', label: 'Left', thumbnail: viewLeftImg },
    { id: 'right', label: 'Right', thumbnail: viewRightImg },
    { id: 'left_front', label: 'Left 45°', thumbnail: viewLeft45Img },
    { id: 'right_front', label: 'Right 45°', thumbnail: viewRight45Img },
    { id: 'top', label: 'Top', thumbnail: viewTopImg },
    { id: 'bottom', label: 'Bottom', thumbnail: viewBottomImg }
];

const VIEW_SLOT_IDS = new Set<string>(VIEW_SLOTS.map((slot) => slot.id));

const isViewSlotId = (value: string): value is ViewSlotId => VIEW_SLOT_IDS.has(value);

export const getSupportedMultiViewSlotIds = (model: Pick<ModelGeneration3DModel, 'slots'> | undefined): ViewSlotId[] => {
    if (!model) return VIEW_SLOTS.map((slot) => slot.id);

    const supportedSlots = new Set(model.slots.filter(isViewSlotId));
    return VIEW_SLOTS.map((slot) => slot.id).filter((slotId) => supportedSlots.has(slotId));
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

const SlotsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
`;

const Slot = styled.div<{ $dragging: boolean; $filled: boolean; $required: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 0;
    height: 82px;
    padding: 8px;
    border: 1px solid ${(props) => (props.$dragging ? '#3b82f6' : props.$required ? '#bfdbfe' : '#e5e7eb')};
    border-radius: 12px;
    background: ${(props) => (props.$dragging ? '#eff6ff' : props.$required ? '#f8fbff' : '#ffffff')};
    cursor: pointer;
    overflow: hidden;
    transition:
        border-color 0.15s ease,
        background 0.15s ease,
        transform 0.15s ease;

    &:hover {
        border-color: #3b82f6;
        background: #eff6ff;
        transform: translateY(-1px);
    }
`;

const Preview = styled.img<{ $cover?: boolean }>`
    ${(props) =>
        props.$cover
            ? `
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
		`
            : `
			width: 40px;
			height: 32px;
			object-fit: contain;
		`}
`;

const SlotLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: #374151;
`;

const Badge = styled.span<{ $required: boolean }>`
    padding: 2px 5px;
    border-radius: 6px;
    background: ${(props) => (props.$required ? '#3b82f6' : '#f3f4f6')};
    color: ${(props) => (props.$required ? '#ffffff' : '#9ca3af')};
    font-size: 8px;
    font-weight: 700;
    text-transform: uppercase;
`;

const AddIcon = styled(Icon)`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 14px;
    height: 14px;
    color: #3b82f6;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 1px solid #ffffff;
    border-radius: 50%;
    background: rgba(17, 24, 39, 0.72);
    color: #ffffff;
    cursor: pointer;

    ${Icon} {
        width: 11px;
        height: 11px;
        fill: currentColor;
    }
`;

const Help = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #6b7280;

    ${Icon} {
        width: 15px;
        height: 15px;
        fill: #3b82f6;
    }
`;

const HiddenInput = styled.input`
    display: none;
`;

interface MultiViewDropZoneProps {
    values: readonly MultiViewInputData[];
    onChange: (values: MultiViewInputData[]) => void;
    slotIds?: readonly ViewSlotId[];
    onLoadingChange?: (loading: boolean) => void;
}

export const MultiViewDropZone: FC<MultiViewDropZoneProps> = ({ values, onChange, slotIds, onLoadingChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const targetSlotRef = useRef<ViewSlotId>('front');
    const [draggingSlot, setDraggingSlot] = useState<ViewSlotId | null>(null);
    const visibleSlots = useMemo(() => {
        const supported = new Set(slotIds ?? VIEW_SLOTS.map((slot) => slot.id));
        return VIEW_SLOTS.filter((slot) => supported.has(slot.id));
    }, [slotIds]);
    const previewUrls = useMemo(() => new Map(values.map((value) => [value.viewType, URL.createObjectURL(value.file)])), [values]);

    useEffect(() => {
        return () => previewUrls.forEach((url) => URL.revokeObjectURL(url));
    }, [previewUrls]);

    const loadFile = useCallback(
        async (slotId: ViewSlotId, file: File) => {
            if (!file.type.startsWith('image/')) return;
            if (file.size > MAX_3D_UPLOAD_SIZE_BYTES) {
                showErrorNotification({ message: T._(`File size must not exceed ${MAX_3D_UPLOAD_SIZE_MB} MB.`, 'Admin') });
                return;
            }

            onLoadingChange?.(true);
            try {
                const { width, height } = await getImageDimensions(file);
                if (width > MAX_3D_IMAGE_DIMENSION || height > MAX_3D_IMAGE_DIMENSION) {
                    showErrorNotification({
                        message: T._(`Image dimensions must not exceed ${MAX_3D_IMAGE_DIMENSION}×${MAX_3D_IMAGE_DIMENSION} pixels.`, 'Admin')
                    });
                    return;
                }
                onChange([...values.filter((value) => value.viewType !== slotId), { file, viewType: slotId }]);
            } catch {
                showErrorNotification({ message: T._('Unable to read image. Please try a different file.', 'Admin') });
            } finally {
                onLoadingChange?.(false);
            }
        },
        [onChange, onLoadingChange, values]
    );

    const openPicker = useCallback((slotId: ViewSlotId) => {
        targetSlotRef.current = slotId;
        inputRef.current?.click();
    }, []);

    return (
        <Wrapper>
            <SlotsGrid>
                {visibleSlots.map((slot) => {
                    const value = values.find((item) => item.viewType === slot.id);
                    return (
                        <Slot
                            key={slot.id}
                            role='button'
                            aria-label={T._(`${slot.label} view`, 'Admin')}
                            tabIndex={value ? -1 : 0}
                            $dragging={draggingSlot === slot.id}
                            $filled={!!value}
                            $required={!!slot.required}
                            onClick={() => !value && openPicker(slot.id)}
                            onKeyDown={(event) => {
                                if (!value && (event.key === 'Enter' || event.key === ' ')) {
                                    event.preventDefault();
                                    openPicker(slot.id);
                                }
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setDraggingSlot(slot.id);
                            }}
                            onDragLeave={() => setDraggingSlot(null)}
                            onDrop={(event) => {
                                event.preventDefault();
                                setDraggingSlot(null);
                                const file = event.dataTransfer.files[0];
                                if (file) void loadFile(slot.id, file);
                            }}
                        >
                            {value ? (
                                <>
                                    <Preview $cover src={previewUrls.get(slot.id)} alt={value.file.name} />
                                    <RemoveButton
                                        type='button'
                                        aria-label={T._(`Remove ${slot.label} view`, 'Admin')}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onChange(values.filter((item) => item.viewType !== slot.id));
                                        }}
                                    >
                                        <Icon>
                                            <CloseIcon />
                                        </Icon>
                                    </RemoveButton>
                                </>
                            ) : (
                                <>
                                    <AddIcon>
                                        <PlusIcon />
                                    </AddIcon>
                                    <Preview src={slot.thumbnail} alt='' />
                                    <SlotLabel>{T._(slot.label, 'Admin')}</SlotLabel>
                                    <Badge $required={!!slot.required}>{T._(slot.required ? 'Required' : 'Optional', 'Admin')}</Badge>
                                </>
                            )}
                        </Slot>
                    );
                })}
            </SlotsGrid>
            <Help>
                <Icon>
                    <InfoIcon />
                </Icon>
                {T._('Front is required. Additional views improve 3D reconstruction accuracy.', 'Admin')}
            </Help>
            <HiddenInput
                ref={inputRef}
                type='file'
                accept='image/png,image/jpeg,image/webp'
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void loadFile(targetSlotRef.current, file);
                    event.target.value = '';
                }}
            />
        </Wrapper>
    );
};

export default MultiViewDropZone;
